import React, { useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Router } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import loadablePagePart from '%app/js/common/services/loadablePagePart';
import ErrorBoundary from '%app/js/common/components/ErrorBoundary';
import Loading from '%app/js/common/components/material/Loading';
import ensureTranslations from '%app/js/common/services/translationLoader';
import mountReactRoot from '%app/js/common/services/mountReactRoot';
import useAppearance from '%evp_theme/brandbook/appearance/useAppearance';
import { createRedesignTheme, createChromeTheme } from './theme';
import readHostRootFontSize from './services/readHostRootFontSize';
import initSentry from './services/initSentry';
import { ServerDataProvider, readServerData } from './context/ServerDataContext';
import initPostHog from '../posthog/initPostHog';
import subscribePostHogToConsent from '../posthog/subscribePostHogToConsent';
import App from './App';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsent';
import DirectLinkNotFound from './components/DirectLinkNotFound';
import PageNotice from './components/PageNotice';
import PageLoadFailureBoundary from './components/PageLoadFailureBoundary';
import { HeaderOverlayProvider } from './components/Header/HeaderOverlayContext';
import { readChallengeFromDataset } from './services/captchaChallenge';
import readRedesignTranslations from './services/readRedesignTranslations';
import readRedesignFooterLinks from './services/readRedesignFooterLinks';
import useLocaleReloadHistory from './services/useLocaleReloadHistory';
import {
    REDESIGN_ROOT_ID,
    REDESIGN_HEADER_ROOT_ID,
    REDESIGN_FOOTER_ROOT_ID,
    REDESIGN_NOT_FOUND_ROOT_ID,
} from './services/readRedesignRootAttribute';

const GET_YOUR_TICKET_ROOT_ID = 'get-your-ticket-root';

const CHROME_FONT_FAMILIES = ['Inter', 'Material Symbols Rounded'];
const CHROME_FONT_POLL_INTERVAL_MS = 50;
const CHROME_FONT_WAIT_TIMEOUT_MS = 3000;
const CHROME_READY_ATTRIBUTE = 'data-redesign-chrome-ready';

const AsyncGetYourTicketPage = loadablePagePart(
    () => import('./pages/GetYourTicketPage'),
    { fallback: <Loading /> },
);

function areChromeFontsLoaded() {
    if (typeof document.fonts === 'undefined' || typeof document.fonts.forEach !== 'function') {
        return true;
    }

    const loadedFamilies = new Set();
    document.fonts.forEach((fontFace) => {
        if (fontFace.status === 'loaded') {
            loadedFamilies.add(fontFace.family.replace(/["']/g, ''));
        }
    });

    return CHROME_FONT_FAMILIES.every(family => loadedFamilies.has(family));
}

function RedesignShell({
    themeForScheme,
    children,
    readyRootId = null,
    serverData = null,
}) {
    const { scheme } = useAppearance();
    const shellTheme = useMemo(() => themeForScheme(scheme), [themeForScheme, scheme]);

    useLayoutEffect(() => {
        if (readyRootId === null) {
            return undefined;
        }

        const root = document.getElementById(readyRootId);
        if (root === null) {
            return undefined;
        }

        if (areChromeFontsLoaded()) {
            root.setAttribute(CHROME_READY_ATTRIBUTE, '');
            return undefined;
        }

        let pollsRemaining = CHROME_FONT_WAIT_TIMEOUT_MS / CHROME_FONT_POLL_INTERVAL_MS;
        const pollTimer = setInterval(() => {
            pollsRemaining -= 1;
            if (areChromeFontsLoaded() || pollsRemaining <= 0) {
                clearInterval(pollTimer);
                root.setAttribute(CHROME_READY_ATTRIBUTE, '');
            }
        }, CHROME_FONT_POLL_INTERVAL_MS);

        return () => clearInterval(pollTimer);
    }, [readyRootId]);

    return (
        <ThemeProvider theme={shellTheme}>
            <CssBaseline />
            <ErrorBoundary>
                <ServerDataProvider value={serverData}>
                    {children}
                </ServerDataProvider>
            </ErrorBoundary>
        </ThemeProvider>
    );
}

RedesignShell.propTypes = {
    themeForScheme: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    readyRootId: PropTypes.string,
    serverData: PropTypes.shape({}),
};

function ChromeOnly() {
    const [translations] = useState(readRedesignTranslations);
    const [footerLinks] = useState(readRedesignFooterLinks);
    const history = useLocaleReloadHistory();
    const footerRoot = useMemo(() => document.getElementById(REDESIGN_FOOTER_ROOT_ID), []);

    return (
        <>
            <div>
                <HeaderOverlayProvider>
                    <Header translations={translations} mobileSearchEnabled={false} />
                </HeaderOverlayProvider>
            </div>
            <ErrorBoundary fallback={null}>
                <PageNotice />
            </ErrorBoundary>
            {footerRoot && createPortal(
                <Router history={history}>
                    <Footer translations={translations} footerLinks={footerLinks} />
                    <CookieConsentBanner />
                </Router>,
                footerRoot,
            )}
        </>
    );
}

const serverData = readServerData();

initSentry(serverData.appSettings);
initPostHog(serverData.appSettings);
subscribePostHogToConsent(serverData.appSettings);

ensureTranslations([
    'TicketFrontendBundle',
    'PayseraServiceAgreementBundle',
]).then(() => {
    mountReactRoot(
        REDESIGN_ROOT_ID,
        <RedesignShell themeForScheme={createRedesignTheme} readyRootId={REDESIGN_ROOT_ID} serverData={serverData}>
            <App />
        </RedesignShell>,
        { optional: true },
    );

    const hostRootFontSize = readHostRootFontSize();
    const chromeThemeForScheme = scheme => createChromeTheme(hostRootFontSize, scheme);

    mountReactRoot(
        REDESIGN_HEADER_ROOT_ID,
        <RedesignShell
            themeForScheme={chromeThemeForScheme}
            readyRootId={REDESIGN_HEADER_ROOT_ID}
            serverData={serverData}
        >
            <ChromeOnly />
        </RedesignShell>,
        { optional: true },
    );

    mountReactRoot(
        REDESIGN_NOT_FOUND_ROOT_ID,
        <RedesignShell themeForScheme={chromeThemeForScheme} serverData={serverData}>
            <DirectLinkNotFound />
        </RedesignShell>,
        { optional: true },
    );

    const getYourTicketRoot = document.getElementById(GET_YOUR_TICKET_ROOT_ID);
    mountReactRoot(
        GET_YOUR_TICKET_ROOT_ID,
        <RedesignShell themeForScheme={chromeThemeForScheme} serverData={serverData}>
            <PageLoadFailureBoundary translations={readRedesignTranslations()}>
                <AsyncGetYourTicketPage
                    organizerContactsUrl={getYourTicketRoot ? getYourTicketRoot.dataset.organizerContactsUrl : ''}
                    initialChallenge={readChallengeFromDataset(getYourTicketRoot ? getYourTicketRoot.dataset : null)}
                />
            </PageLoadFailureBoundary>
        </RedesignShell>,
        { optional: true },
    );
});

export { RedesignShell, ChromeOnly };
